import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatFlightNumberAnswer {
  flightNumber: string;
}

const DEFAULT_MAX_LENGTH = 50;

/**
 * Renders the flight-number free-text widget inside the chat widget — used by
 * Groundspan/CMAC taxi bookings to optionally pass the passenger's flight
 * number. Mirrors the manual LightUK SI form's "Flight Number" field
 * (per-item supplementary schema's `flightNumber`) — free text, optional,
 * single combined carrier+number value (e.g. "BA2490"), unlike the Sabre
 * car-hire `flightDetails` widget's separate carrier/number pair.
 */
@Component({
  selector: 'app-chat-flight-number-input',
  standalone: false,
  templateUrl: './chat-flight-number-input.component.html',
  styleUrls: ['./chat-flight-number-input.component.scss']
})
export class ChatFlightNumberInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() savedValue: string | null = null;
  @Input() required = false;
  @Input() maxLength = DEFAULT_MAX_LENGTH;
  @Output() answer = new EventEmitter<ChatFlightNumberAnswer>();
  @Output() skip = new EventEmitter<void>();

  flightNumber = '';
  /** Shown alongside the (disabled) field once this widget instance is locked. */
  chosenSummary: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the widget whenever the underlying field changes (new question)
    // — but NOT merely because `locked` toggled, which would wipe out the
    // entered text just as we want to display it as read-only.
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    this.flightNumber = this.savedValue ?? '';
    this.chosenSummary = null;
  }

  get isMandatory(): boolean {
    return this.required === true;
  }

  get isValid(): boolean {
    return this.isMandatory ? this.flightNumber.trim().length > 0 : true;
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    const trimmed = this.flightNumber.trim();
    if (!trimmed) {
      // Only reachable for an optional field with nothing entered.
      this.chosenSummary = 'Skipped';
      this.skip.emit();
      return;
    }
    this.chosenSummary = trimmed;
    this.answer.emit({ flightNumber: trimmed });
  }
}
